import fetchWithTimeout from '../../../../../util/fetchWithTimeout';
import { MERCATELY_API } from '../../../../../constants/AppConstants';
import {
  buildVoiceCallControlBody,
  buildVoiceHttpAuthHeaders,
  getMercatelySessionJwt,
  getVoiceIdentityFromJwt
} from '../auth/voiceAuth';
import { getCurrentVoiceRetailerUserId, getVoicePlatformApiBaseUrl } from '../config/voiceEnvConfig';
import getOrCreateVoiceTabSessionId from '../webrtc/voiceTabSession';

const JSON_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json'
};

function assertVoiceApiConfigured() {
  const baseUrl = getVoicePlatformApiBaseUrl();
  if (!baseUrl) {
    throw new Error(
      'Voice Platform API URL is not configured. Set ENV.VOICE_PLATFORM_API_URL '
      + '(https://voice-app.mercately.com).'
    );
  }
  return baseUrl;
}

async function voiceAuthHeaders() {
  return buildVoiceHttpAuthHeaders(JSON_HEADERS);
}

async function voiceTabSessionBody() {
  return buildVoiceCallControlBody();
}

/**
 * @param {Response} response
 * @returns {Promise<unknown>}
 */
async function parseJsonResponse(response) {
  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

/**
 * voice-service uses TransformInterceptor ({ response, error }); Mercately routes expect the inner body.
 *
 * @param {unknown} data
 * @returns {unknown}
 */
function unwrapVoicePlatformEnvelope(data) {
  if (
    data
    && typeof data === 'object'
    && Object.prototype.hasOwnProperty.call(data, 'response')
    && typeof data.error === 'string'
  ) {
    return data.response;
  }
  return data;
}

/**
 * @param {string} path
 * @param {{ method?: string, body?: unknown }} [options]
 * @returns {Promise<unknown>}
 */
async function voicePlatformRequest(path, options = {}) {
  const baseUrl = assertVoiceApiConfigured();
  const url = `${baseUrl}${path}`;
  const method = options.method ?? 'GET';
  const headers = await voiceAuthHeaders();

  const response = await fetchWithTimeout(url, {
    method,
    headers,
    credentials: 'omit',
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    timeoutMs: options.timeoutMs
  });

  const data = await parseJsonResponse(response);

  if (!response.ok) {
    const envelope = data && typeof data === 'object' ? data : {};
    const { error: errPayload, message: envelopeMessage } = envelope;
    let message = envelopeMessage;
    if (typeof errPayload === 'string') {
      message = errPayload;
    } else if (errPayload && typeof errPayload === 'object') {
      message = errPayload.error ?? errPayload.message ?? message;
    }
    if (!message) {
      message = `HTTP error! Status: ${response.status}`;
    }
    const error = new Error(typeof message === 'string' ? message : `HTTP error! Status: ${response.status}`);
    error.status = response.status;
    error.body = data;
    error.voiceErrorCode = extractVoicePlatformErrorCode(data);
    throw error;
  }

  return unwrapVoicePlatformEnvelope(data);
}

/**
 * Nest AllExceptionsFilter: `{ response: {}, error: { ok, error: 'call_ended', ... } }`.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function isVoiceAnswerConflictAcceptable(error) {
  if (!error || typeof error !== 'object' || error.status !== 409) {
    return false;
  }
  const code = error.voiceErrorCode ?? extractVoicePlatformErrorCode(error.body);
  return code === 'call_ended' || code === 'call_already_answered';
}

/**
 * @param {unknown} data
 * @returns {string|undefined}
 */
function extractVoicePlatformErrorCode(data) {
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  const errPayload = data.error;
  if (typeof errPayload === 'object' && errPayload && typeof errPayload.error === 'string') {
    return errPayload.error;
  }
  if (typeof errPayload === 'string') {
    return errPayload;
  }
  return undefined;
}

/** @type {number} Bridge can wait for Meta ACK + FreeSWITCH originate. */
const VOICE_ANSWER_REQUEST_TIMEOUT_MS = 120_000;

/**
 * @param {string} callControlId
 * @returns {Promise<unknown>}
 */
async function answerCall(callControlId) {
  return voicePlatformRequest(
    `/voice/calls/${encodeURIComponent(callControlId)}/answer`,
    { method: 'POST', body: await voiceTabSessionBody(), timeoutMs: VOICE_ANSWER_REQUEST_TIMEOUT_MS }
  );
}

/**
 * @param {string} callControlId
 * @param {{ endedReason?: string }} [options]
 */
async function hangupCall(callControlId, options = {}) {
  const body = await voiceTabSessionBody();
  if (options.endedReason) {
    body.ended_reason = options.endedReason;
  }
  return voicePlatformRequest(
    `/voice/calls/${encodeURIComponent(callControlId)}/hangup`,
    { method: 'POST', body }
  );
}

/**
 * @param {{ to: string, from?: string, customerId?: string|number, appId?: string }} payload
 * @returns {Promise<{ callControlId: string, [key: string]: unknown }>}
 */
async function createCall(payload) {
  const from = typeof payload.from === 'string' ? payload.from.trim() : '';
  if (!from) {
    throw new Error('unknown_from_number');
  }

  const body = {
    to: payload.to,
    from,
    ...(await voiceTabSessionBody())
  };
  if (payload.customerId != null && payload.customerId !== '') {
    body.customer_id = payload.customerId;
  }
  if (payload.appId) {
    body.app_id = String(payload.appId);
  }

  const response = await voicePlatformRequest('/voice/calls', {
    method: 'POST',
    body
  });

  const normalized = response?.payload ?? response ?? {};
  const callControlId = normalized?.callControlId
    ?? normalized?.call_control_id
    ?? normalized?.data?.callControlId
    ?? normalized?.data?.call_control_id;

  if (typeof callControlId !== 'string' || !callControlId) {
    throw new Error('Invalid create call response: missing callControlId');
  }

  return {
    ...normalized,
    callControlId
  };
}

/**
 * FreeSWITCH / JsSIP credentials from voice-service (one SIP user per browser tab).
 *
 * @param {string} [tabSessionId]
 * @returns {Promise<{ sip: { wsUrl: string, uri: string, password: string, username: string, domain: string, registrarUri?: string }, tabSessionId: string, remoteMediaElementId?: string, iceServers?: RTCIceServer[], iceTransportPolicy?: 'all'|'relay' }>}
 */
async function fetchWebRtcCredentials(tabSessionId) {
  const sessionId = tabSessionId || getOrCreateVoiceTabSessionId();
  const query = new URLSearchParams({ tab_session_id: sessionId });
  const token = await getMercatelySessionJwt();
  const { retailerUserId } = getVoiceIdentityFromJwt(token, {
    retailerUserId: getCurrentVoiceRetailerUserId()
  });
  if (retailerUserId) {
    query.set('retailer_user_id', retailerUserId);
  }
  const response = await voicePlatformRequest(`/voice/webrtc/credentials?${query.toString()}`);

  const sip = response?.sip ?? response?.data?.sip;
  const wsUrl = sip?.wsUrl ?? sip?.ws_url;
  const uri = sip?.uri;
  const password = sip?.password;
  if (!sip || typeof wsUrl !== 'string' || typeof uri !== 'string' || typeof password !== 'string') {
    throw new Error('Invalid WebRTC credentials response');
  }

  const normalizedSip = {
    ...sip,
    wsUrl,
    uri,
    password,
    username: sip.username ?? sip.user_name,
    domain: sip.domain,
    registrarUri: sip.registrarUri ?? sip.registrar_uri
  };

  const remoteMediaElementId = response?.remote_media_element_id
    ?? response?.remoteMediaElementId
    ?? undefined;

  const rawIceServers = response?.iceServers ?? response?.ice_servers;
  const iceServers = Array.isArray(rawIceServers)
    ? rawIceServers.map((entry) => {
      const urls = entry?.urls ?? entry?.url;
      if (!urls) return null;
      const server = { urls };
      if (typeof entry.username === 'string' && entry.username) {
        server.username = entry.username;
      }
      const credential = entry.credential ?? entry.password;
      if (typeof credential === 'string' && credential) {
        server.credential = credential;
      }
      return server;
    }).filter(Boolean)
    : undefined;

  const iceTransportPolicy = response?.iceTransportPolicy
    ?? response?.ice_transport_policy;

  return {
    sip: normalizedSip,
    tabSessionId: response?.tabSessionId ?? response?.tab_session_id ?? sessionId,
    remoteMediaElementId: typeof remoteMediaElementId === 'string' ? remoteMediaElementId : undefined,
    ...(iceServers?.length ? { iceServers } : {}),
    ...(iceTransportPolicy === 'relay' || iceTransportPolicy === 'all'
      ? { iceTransportPolicy }
      : {})
  };
}

function unwrapTenantConfigList(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.tenantConfigs)) return response.tenantConfigs;
  if (Array.isArray(response?.tenant_configs)) return response.tenant_configs;
  return [];
}

/**
 * WhatsApp business lines for the authenticated retailer (JWT).
 *
 * @returns {Promise<Array<{ businessE164?: string, wabaPhoneNumberId?: string, enabled?: boolean }>>}
 */
async function fetchTenantConfigs() {
  try {
    return unwrapTenantConfigList(await voicePlatformRequest('/api/v1/voice/tenant-configs'));
  } catch (error) {
    if (error?.status === 404) {
      return unwrapTenantConfigList(await voicePlatformRequest('/voice/tenant-configs'));
    }
    throw error;
  }
}

/**
 * Live call board snapshot for the authenticated retailer (supervisor wallboard).
 *
 * @returns {Promise<{ serverTime?: string, calls?: Array<object> }|Array<object>>}
 */
async function fetchActiveCalls() {
  return voicePlatformRequest('/api/v1/voice/calls/active');
}

/**
 * Retailer SIP readiness (`{ sipEnabled }`) via the Mercately session-auth proxy
 * (`GET /api/v1/bsp/voice`). Optional `appId` scopes the check to one BSP line (multi-number).
 * Without `appId`, multi-number retailers may receive aggregate `{ sipEnabled, lines }`.
 *
 * @param {{ appId?: string }} [options]
 * @returns {Promise<{ sipEnabled?: boolean, lines?: Array<object> }>}
 */
async function fetchSipStatus(options = {}) {
  const query = new URLSearchParams();
  if (options.appId) {
    query.set('app_id', String(options.appId));
  }
  const qs = query.toString();
  const url = `${MERCATELY_API}/bsp/voice${qs ? `?${qs}` : ''}`;

  const response = await fetchWithTimeout(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    timeoutMs: 15_000
  });

  const data = await parseJsonResponse(response);
  if (!response.ok) {
    const error = new Error(`SIP status check failed: HTTP ${response.status}`);
    error.status = response.status;
    error.body = data;
    throw error;
  }

  return data;
}

/**
 * Registers voice SIP for the current retailer via the Mercately session-auth
 * proxy (`POST /api/v1/bsp/voice/register_sip`). The proxy calls
 * SipClient#register_voice_sip (fuerza el partner Voice) y habilita
 * voice_platform_calls al retailer.
 *
 * Legacy (single-number) no requiere parámetros: el backend usa gupshup_app_id.
 * Multinúmero debe enviar la línea (appId + platformRetailerId).
 *
 * @param {{ appId?: string, platformRetailerId?: string, integrationPlatform?: string }} [params]
 * @returns {Promise<{ sipRegistered?: boolean }>}
 */
const SIP_REGISTER_TIMEOUT_MS = 60_000;

async function registerSip({ appId, platformRetailerId, integrationPlatform } = {}) {
  const body = {};
  if (appId) body.app_id = appId;
  if (platformRetailerId) body.platform_retailer_id = platformRetailerId;
  if (integrationPlatform) body.integration_platform = integrationPlatform;

  const csrfToken = document.querySelector('[name=csrf-token]')?.content;
  const response = await fetchWithTimeout(`${MERCATELY_API}/bsp/voice/register_sip`, {
    method: 'POST',
    headers: {
      ...JSON_HEADERS,
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
    },
    credentials: 'same-origin',
    body: JSON.stringify(body),
    timeoutMs: SIP_REGISTER_TIMEOUT_MS
  });

  const data = await parseJsonResponse(response);
  if (!response.ok) {
    const error = new Error(data?.error || `SIP registration failed: HTTP ${response.status}`);
    error.status = response.status;
    error.body = data;
    throw error;
  }

  return data;
}

const VoiceCallsApiClient = {
  createCall,
  answerCall,
  hangupCall,
  fetchActiveCalls,
  fetchSipStatus,
  registerSip,
  fetchWebRtcCredentials,
  fetchTenantConfigs
};

export default VoiceCallsApiClient;
